// src/pages/clasificacion_productos.js
//
// Admin page for the hierarchical product classification catalog:
//   1 Producto -> 2 Categoría -> (3 Subproducto, 4 Modalidad, 5 Tipo)
// Levels 3-5 hang from a Categoría, or directly from a Producto when that
// Producto has no categories. Data comes from /api/clasificacion-productos/.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button, Input, Alert, Spinner } from "reactstrap";
import axios from "axios";
import { API_URL_clasificacion_productos } from "../constants";
import { getAuthHeaders } from "../utils/auth";
import { Page, PageHeader, IconPlus, IconPencil, IconTrash } from "../components/ui";

const NIVEL = {
  PRODUCTO: 1,
  CATEGORIA: 2,
  SUBPRODUCTO: 3,
  MODALIDAD: 4,
  TIPO: 5,
};

const NIVEL_LABEL = {
  1: "Producto",
  2: "Categoría",
  3: "Subproducto",
  4: "Modalidad",
  5: "Tipo",
};

const GRUPOS_HOJA = [
  { nivel: NIVEL.SUBPRODUCTO, titulo: "Subproductos" },
  { nivel: NIVEL.MODALIDAD, titulo: "Modalidades" },
  { nivel: NIVEL.TIPO, titulo: "Tipos" },
];

const normalize = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

const sortNodes = (a, b) =>
  (a.orden ?? 0) - (b.orden ?? 0) ||
  String(a.nombre).localeCompare(String(b.nombre), "es", { sensitivity: "base" });

// Turn a DRF error payload into one readable Spanish sentence.
function errorMessage(e, fallback) {
  const data = e?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (data.detail) return String(data.detail);
  const parts = [];
  Object.keys(data).forEach((k) => {
    const v = data[k];
    const msg = Array.isArray(v) ? v.join(" ") : String(v);
    parts.push(k === "non_field_errors" ? msg : `${k}: ${msg}`);
  });
  return parts.length ? parts.join(" ") : fallback;
}

const CSS = `
.cp-note { background: var(--lssi-subtle); border: 1px solid var(--lssi-rule); border-radius: 10px; padding: 12px 16px; color: var(--lssi-muted); font-size: 0.9rem; margin-bottom: 16px; }
.cp-toolbar { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 16px; }
.cp-toolbar .cp-search { max-width: 360px; }
.cp-tree { display: flex; flex-direction: column; gap: 12px; }
.cp-producto { background: var(--lssi-surface); border: 1px solid var(--lssi-rule); border-radius: 12px; overflow: hidden; }
.cp-producto-head { display: flex; align-items: center; gap: 8px; padding: 10px 14px; background: var(--lssi-subtle); border-bottom: 1px solid transparent; }
.cp-producto.cp-open > .cp-producto-head { border-bottom-color: var(--lssi-rule); }
.cp-producto-body { padding: 10px 14px 14px 14px; }
.cp-caret { border: 0; background: transparent; width: 26px; height: 26px; border-radius: 6px; color: var(--lssi-muted); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; }
.cp-caret:hover { background: var(--lssi-rule); color: var(--lssi-ink); }
.cp-caret svg { transition: transform .15s ease; }
.cp-caret.cp-open svg { transform: rotate(90deg); }
.cp-name { font-weight: 600; color: var(--lssi-ink); }
.cp-producto-head .cp-name { font-size: 1.05rem; }
.cp-categoria { border: 1px solid var(--lssi-rule); border-radius: 10px; margin-top: 10px; overflow: hidden; }
.cp-categoria-head { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #fff; }
.cp-categoria.cp-open > .cp-categoria-head { border-bottom: 1px solid var(--lssi-rule); background: var(--lssi-subtle); }
.cp-categoria-body { padding: 8px 12px 12px 12px; }
.cp-count { color: var(--lssi-muted); font-size: 0.8rem; font-weight: 400; margin-left: 4px; }
.cp-row-actions { margin-left: auto; display: inline-flex; gap: 4px; align-items: center; flex-wrap: wrap; }
.cp-icon-btn { border: 1px solid transparent; background: transparent; color: var(--lssi-muted); width: 26px; height: 26px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; }
.cp-icon-btn:hover { background: var(--lssi-rule); color: var(--lssi-ink); }
.cp-icon-btn.cp-danger:hover { background: var(--lssi-red-tint); color: var(--lssi-red-dark); }
.cp-icon-btn:disabled { opacity: .4; cursor: default; }
.cp-link-btn { border: 0; background: transparent; color: var(--lssi-muted); font-size: 0.78rem; padding: 2px 6px; border-radius: 6px; cursor: pointer; white-space: nowrap; }
.cp-link-btn:hover { background: var(--lssi-rule); color: var(--lssi-ink); }
.cp-badge { display: inline-block; font-size: 0.7rem; font-weight: 600; letter-spacing: .02em; padding: 1px 7px; border-radius: 999px; background: var(--lssi-rule); color: var(--lssi-muted); margin-left: 6px; vertical-align: middle; }
.cp-inactive > .cp-name, .cp-inactive .cp-chip-name { color: var(--lssi-muted); text-decoration: line-through; text-decoration-color: #c4c8cf; }
.cp-inactive { opacity: .75; }
.cp-groups { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; margin-top: 8px; }
.cp-group { border: 1px dashed var(--lssi-rule); border-radius: 10px; padding: 8px 10px 10px 10px; min-width: 0; }
.cp-group-title { display: flex; align-items: center; gap: 6px; font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--lssi-muted); margin-bottom: 6px; }
.cp-group-title .cp-add { margin-left: auto; }
.cp-subgrupo { font-size: 0.75rem; font-weight: 600; color: var(--lssi-muted); margin: 8px 0 4px 2px; }
.cp-chips { display: flex; flex-direction: column; gap: 2px; }
.cp-chip { display: flex; align-items: center; gap: 6px; padding: 3px 4px 3px 8px; border-radius: 8px; font-size: 0.875rem; color: var(--lssi-ink); }
.cp-chip:hover { background: var(--lssi-subtle); }
.cp-chip .cp-chip-name { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
.cp-meta { flex: 0 0 auto; font-size: 0.75rem; color: var(--lssi-muted); background: var(--lssi-subtle); border: 1px solid var(--lssi-rule); border-radius: 999px; padding: 0 7px; }
.cp-chip .cp-row-actions { opacity: 0; }
.cp-chip:hover .cp-row-actions, .cp-chip:focus-within .cp-row-actions, .cp-chip.cp-busy .cp-row-actions { opacity: 1; }
.cp-empty { color: var(--lssi-muted); font-size: 0.8rem; font-style: italic; padding: 2px 4px; }
.cp-add { border: 1px dashed var(--lssi-rule); background: transparent; color: var(--lssi-muted); font-size: 0.78rem; padding: 2px 8px; border-radius: 999px; cursor: pointer; white-space: nowrap; display: inline-flex; align-items: center; gap: 2px; }
.cp-add:hover { border-color: var(--lssi-red); color: var(--lssi-red); }
.cp-add svg { width: 13px; height: 13px; }
.cp-inline-form { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; margin: 6px 0; }
.cp-inline-form .form-control { height: 30px; padding: 2px 8px; font-size: 0.875rem; max-width: 260px; }
.cp-inline-form .btn { padding: 2px 10px; font-size: 0.8rem; }
.cp-inline-form .cp-form-error { width: 100%; color: var(--lssi-red-dark); font-size: 0.78rem; }
.cp-rename { display: inline-flex; gap: 6px; align-items: center; flex: 1 1 auto; }
.cp-rename .form-control { height: 28px; padding: 2px 8px; font-size: 0.875rem; max-width: 320px; }
.cp-confirm { display: inline-flex; align-items: center; gap: 6px; font-size: 0.8rem; color: var(--lssi-red-dark); background: var(--lssi-red-tint); padding: 2px 8px; border-radius: 999px; }
.cp-confirm button { border: 0; background: transparent; font-weight: 600; color: var(--lssi-red-dark); padding: 0 4px; cursor: pointer; }
.cp-confirm button.cp-no { color: var(--lssi-muted); }
.cp-muted { color: var(--lssi-muted); font-size: 0.85rem; }
`;

const IconCaret = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

// ---------------------------------------------------------------------------
// Inline add form (nombre + optional grupo for subproductos)
// ---------------------------------------------------------------------------
function AddForm({ nivel, parent, onCreate, onCancel }) {
  const [nombre, setNombre] = useState("");
  const [grupo, setGrupo] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const submit = async (e) => {
    if (e) e.preventDefault();
    const n = nombre.trim();
    if (!n) return;
    setSaving(true);
    setErr("");
    const payload = { nivel, nombre: n, parent: parent ?? null };
    if (nivel === NIVEL.SUBPRODUCTO && grupo.trim()) payload.grupo = grupo.trim();
    const problem = await onCreate(payload);
    setSaving(false);
    if (problem) {
      setErr(problem);
    } else {
      setNombre("");
      // keep grupo so several subproductos of the same grupo can be added in a row
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const onKey = (e) => {
    if (e.key === "Escape") onCancel();
  };

  return (
    <form className="cp-inline-form" onSubmit={submit} onKeyDown={onKey}>
      <Input
        innerRef={inputRef}
        bsSize="sm"
        placeholder={`Nombre de ${NIVEL_LABEL[nivel].toLowerCase()}`}
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        disabled={saving}
        maxLength={150}
      />
      {nivel === NIVEL.SUBPRODUCTO && (
        <Input
          bsSize="sm"
          placeholder="Grupo (opcional)"
          value={grupo}
          onChange={(e) => setGrupo(e.target.value)}
          disabled={saving}
          maxLength={100}
          title="Agrupa visualmente varios subproductos, por ejemplo 'Herramientas de taller'"
        />
      )}
      <Button color="primary" size="sm" type="submit" disabled={saving || !nombre.trim()}>
        {saving ? <Spinner size="sm" /> : "Guardar"}
      </Button>
      <Button color="secondary" size="sm" outline type="button" onClick={onCancel} disabled={saving}>
        Cancelar
      </Button>
      {err && <div className="cp-form-error">{err}</div>}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Per-node action cluster: rename / toggle activo / delete (inline confirm)
// ---------------------------------------------------------------------------
function NodeActions({ node, onToggle, onDelete, busy, setBusy, renaming, setRenaming }) {
  const [confirming, setConfirming] = useState(false);

  const toggle = async () => {
    setBusy(true);
    await onToggle(node);
    setBusy(false);
  };

  const doDelete = async () => {
    setBusy(true);
    await onDelete(node);
    setBusy(false);
    setConfirming(false);
  };

  if (renaming) return null;

  return (
    <span className="cp-row-actions" onClick={(e) => e.stopPropagation()}>
      {confirming ? (
        <span className="cp-confirm">
          ¿Borrar?
          <button type="button" onClick={doDelete} disabled={busy}>Sí</button>
          <button type="button" className="cp-no" onClick={() => setConfirming(false)} disabled={busy}>No</button>
        </span>
      ) : (
        <>
          <button
            type="button"
            className="cp-link-btn"
            onClick={toggle}
            disabled={busy}
            title={node.activo ? "Dejar de mostrar en las listas" : "Volver a mostrar en las listas"}
          >
            {node.activo ? "Desactivar" : "Activar"}
          </button>
          <button
            type="button"
            className="cp-icon-btn"
            onClick={() => setRenaming(true)}
            disabled={busy}
            title="Cambiar nombre"
            aria-label="Cambiar nombre"
          >
            <IconPencil />
          </button>
          <button
            type="button"
            className="cp-icon-btn cp-danger"
            onClick={() => setConfirming(true)}
            disabled={busy}
            title="Borrar"
            aria-label="Borrar"
          >
            <IconTrash />
          </button>
        </>
      )}
      {busy && <Spinner size="sm" style={{ width: 14, height: 14, borderWidth: 2 }} />}
    </span>
  );
}

// Name with inline rename. Shared by producto/categoria headers and chips.
function NodeName({ node, className, renaming, setRenaming, onRename }) {
  const [value, setValue] = useState(node.nombre);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (renaming) {
      setValue(node.nombre);
      setErr("");
      setTimeout(() => {
        if (ref.current) {
          ref.current.focus();
          ref.current.select();
        }
      }, 0);
    }
  }, [renaming, node.nombre]);

  const save = async () => {
    const n = value.trim();
    if (!n || n === node.nombre) {
      setRenaming(false);
      return;
    }
    setSaving(true);
    const problem = await onRename(node, n);
    setSaving(false);
    if (problem) setErr(problem);
    else setRenaming(false);
  };

  if (!renaming) {
    return (
      <span className={className}>
        {node.nombre}
        {!node.activo && <span className="cp-badge">Inactivo</span>}
      </span>
    );
  }

  return (
    <span className="cp-rename" onClick={(e) => e.stopPropagation()}>
      <Input
        innerRef={ref}
        bsSize="sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); save(); }
          if (e.key === "Escape") setRenaming(false);
        }}
        disabled={saving}
        maxLength={150}
        invalid={!!err}
        title={err || undefined}
      />
      <button type="button" className="cp-icon-btn" onClick={save} disabled={saving} title="Guardar" aria-label="Guardar">
        {saving ? <Spinner size="sm" style={{ width: 14, height: 14, borderWidth: 2 }} /> : "✓"}
      </button>
      <button type="button" className="cp-icon-btn" onClick={() => setRenaming(false)} disabled={saving} title="Cancelar" aria-label="Cancelar">
        ✕
      </button>
      {err && <span className="cp-form-error" style={{ color: "var(--lssi-red-dark)", fontSize: "0.78rem" }}>{err}</span>}
    </span>
  );
}

// Leaf item (subproducto / modalidad / tipo)
function Chip({ node, actions }) {
  const [renaming, setRenaming] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <div
      className={`cp-chip${node.activo ? "" : " cp-inactive"}${busy || renaming ? " cp-busy" : ""}`}
      title={node.entregable ? `Entregable: ${node.entregable}${node.notas ? ` · ${node.notas}` : ""}` : undefined}
    >
      <NodeName node={node} className="cp-chip-name" renaming={renaming} setRenaming={setRenaming} onRename={actions.rename} />
      {node.duracion_horas ? (
        <span className="cp-meta">{Number(node.duracion_horas)} h</span>
      ) : null}
      <NodeActions
        node={node}
        busy={busy}
        setBusy={setBusy}
        renaming={renaming}
        setRenaming={setRenaming}
        onToggle={actions.toggle}
        onDelete={actions.remove}
      />
    </div>
  );
}

// One of the three labeled groups under a container (Subproductos / Modalidades / Tipos)
function LeafGroup({ titulo, nivel, parent, items, actions, adding, setAdding }) {
  const addKey = `${parent}:${nivel}`;
  const isAdding = adding === addKey;

  let content;
  if (items.length === 0 && !isAdding) {
    content = <div className="cp-empty">Sin elementos</div>;
  } else if (nivel === NIVEL.SUBPRODUCTO && items.some((n) => n.grupo)) {
    // Group subproductos by `grupo`; ungrouped first, then grupos in first-seen order.
    const sinGrupo = items.filter((n) => !n.grupo);
    const grupos = [];
    items.forEach((n) => {
      if (!n.grupo) return;
      let g = grupos.find((x) => x.nombre === n.grupo);
      if (!g) { g = { nombre: n.grupo, items: [] }; grupos.push(g); }
      g.items.push(n);
    });
    content = (
      <>
        {sinGrupo.length > 0 && (
          <div className="cp-chips">{sinGrupo.map((n) => <Chip key={n.pk} node={n} actions={actions} />)}</div>
        )}
        {grupos.map((g) => (
          <div key={g.nombre}>
            <div className="cp-subgrupo">{g.nombre}</div>
            <div className="cp-chips">{g.items.map((n) => <Chip key={n.pk} node={n} actions={actions} />)}</div>
          </div>
        ))}
      </>
    );
  } else {
    content = <div className="cp-chips">{items.map((n) => <Chip key={n.pk} node={n} actions={actions} />)}</div>;
  }

  return (
    <div className="cp-group">
      <div className="cp-group-title">
        {titulo}
        <span className="cp-count">{items.length}</span>
        {!isAdding && (
          <button type="button" className="cp-add" onClick={() => setAdding(addKey)} title={`Agregar ${NIVEL_LABEL[nivel].toLowerCase()}`}>
            <IconPlus /> {NIVEL_LABEL[nivel]}
          </button>
        )}
      </div>
      {content}
      {isAdding && (
        <AddForm nivel={nivel} parent={parent} onCreate={actions.create} onCancel={() => setAdding(null)} />
      )}
    </div>
  );
}

// The three leaf groups for a given container (Categoría, or Producto without categorías)
function LeafGroups({ parent, childrenOf, actions, adding, setAdding }) {
  const kids = childrenOf(parent);
  return (
    <div className="cp-groups">
      {GRUPOS_HOJA.map((g) => (
        <LeafGroup
          key={g.nivel}
          titulo={g.titulo}
          nivel={g.nivel}
          parent={parent}
          items={kids.filter((n) => n.nivel === g.nivel)}
          actions={actions}
          adding={adding}
          setAdding={setAdding}
        />
      ))}
    </div>
  );
}

function CategoriaBlock({ node, childrenOf, actions, isOpen, toggleOpen, adding, setAdding }) {
  const [renaming, setRenaming] = useState(false);
  const [busy, setBusy] = useState(false);
  const total = childrenOf(node.pk).length;
  return (
    <div className={`cp-categoria${isOpen ? " cp-open" : ""}`}>
      <div className={`cp-categoria-head${node.activo ? "" : " cp-inactive"}`} onClick={() => toggleOpen(node.pk)} style={{ cursor: "pointer" }}>
        <button type="button" className={`cp-caret${isOpen ? " cp-open" : ""}`} onClick={(e) => { e.stopPropagation(); toggleOpen(node.pk); }} aria-label={isOpen ? "Contraer" : "Expandir"}>
          <IconCaret />
        </button>
        <NodeName node={node} className="cp-name" renaming={renaming} setRenaming={setRenaming} onRename={actions.rename} />
        {!renaming && <span className="cp-count">{total} {total === 1 ? "elemento" : "elementos"}</span>}
        <NodeActions node={node} busy={busy} setBusy={setBusy} renaming={renaming} setRenaming={setRenaming} onToggle={actions.toggle} onDelete={actions.remove} />
      </div>
      {isOpen && (
        <div className="cp-categoria-body">
          <LeafGroups parent={node.pk} childrenOf={childrenOf} actions={actions} adding={adding} setAdding={setAdding} />
        </div>
      )}
    </div>
  );
}

function ProductoBlock({ node, childrenOf, actions, expanded, toggleOpen, adding, setAdding }) {
  const [renaming, setRenaming] = useState(false);
  const [busy, setBusy] = useState(false);
  const isOpen = expanded.has(node.pk);
  const kids = childrenOf(node.pk);
  const categorias = kids.filter((n) => n.nivel === NIVEL.CATEGORIA);
  const hasCategorias = categorias.length > 0;
  const addCatKey = `${node.pk}:${NIVEL.CATEGORIA}`;
  const isAddingCat = adding === addCatKey;

  return (
    <div className={`cp-producto${isOpen ? " cp-open" : ""}`}>
      <div className={`cp-producto-head${node.activo ? "" : " cp-inactive"}`} onClick={() => toggleOpen(node.pk)} style={{ cursor: "pointer" }}>
        <button type="button" className={`cp-caret${isOpen ? " cp-open" : ""}`} onClick={(e) => { e.stopPropagation(); toggleOpen(node.pk); }} aria-label={isOpen ? "Contraer" : "Expandir"}>
          <IconCaret />
        </button>
        <NodeName node={node} className="cp-name" renaming={renaming} setRenaming={setRenaming} onRename={actions.rename} />
        {!renaming && (
          <span className="cp-count">
            {hasCategorias
              ? `${categorias.length} ${categorias.length === 1 ? "categoría" : "categorías"}`
              : `${kids.length} ${kids.length === 1 ? "elemento" : "elementos"}`}
          </span>
        )}
        <NodeActions node={node} busy={busy} setBusy={setBusy} renaming={renaming} setRenaming={setRenaming} onToggle={actions.toggle} onDelete={actions.remove} />
      </div>
      {isOpen && (
        <div className="cp-producto-body">
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span className="cp-muted">
              {hasCategorias
                ? "Categorías de este producto"
                : "Este producto no tiene categorías; sus subproductos, modalidades y tipos cuelgan directamente de él."}
            </span>
            {!isAddingCat && (
              <button type="button" className="cp-add" onClick={() => setAdding(addCatKey)}>
                <IconPlus /> Categoría
              </button>
            )}
          </div>
          {isAddingCat && (
            <AddForm nivel={NIVEL.CATEGORIA} parent={node.pk} onCreate={actions.create} onCancel={() => setAdding(null)} />
          )}
          {hasCategorias
            ? categorias.map((c) => (
                <CategoriaBlock
                  key={c.pk}
                  node={c}
                  childrenOf={childrenOf}
                  actions={actions}
                  isOpen={expanded.has(c.pk)}
                  toggleOpen={toggleOpen}
                  adding={adding}
                  setAdding={setAdding}
                />
              ))
            : (
              <LeafGroups parent={node.pk} childrenOf={childrenOf} actions={actions} adding={adding} setAdding={setAdding} />
            )}
          {hasCategorias && kids.some((n) => n.nivel > NIVEL.CATEGORIA) && (
            <div style={{ marginTop: 12 }}>
              <div className="cp-muted" style={{ marginBottom: 4 }}>Elementos que cuelgan directamente del producto (sin categoría)</div>
              <LeafGroups parent={node.pk} childrenOf={childrenOf} actions={actions} adding={adding} setAdding={setAdding} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
// Proyectos que la migración automática no pudo clasificar (fase 2). LSSI los
// revisa y los clasifica desde Proyectos → editar; aquí solo se listan.
function ExcepcionesMigracion() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const cargar = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL_clasificacion_productos}migracion/`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      setData(null);
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  if (!data) return null;
  const { resumen, excepciones } = data;
  const pendientes = (excepciones || []).length;
  const reintentar = async () => {
    setBusy(true); setMsg("");
    try {
      const res = await axios.post(`${API_URL_clasificacion_productos}migracion/`, { aplicar: true }, { headers: getAuthHeaders() });
      setMsg(`${res.data.clasificados} proyecto(s) clasificados automáticamente.`);
      await cargar();
    } catch (e) {
      setMsg("No se pudo correr la clasificación automática.");
    } finally { setBusy(false); }
  };
  return (
    <div className="cp-note" style={{ color: "var(--lssi-ink)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <strong>Proyectos sin clasificación nueva: {pendientes}</strong>
        <span className="cp-muted">· {resumen?.ya_clasificados ?? 0} ya clasificados · {resumen?.clasificables ?? 0} pendientes de aplicar</span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {(resumen?.clasificables ?? 0) > 0 && (
            <Button size="sm" color="primary" onClick={reintentar} disabled={busy}>Clasificar automáticamente</Button>
          )}
          {pendientes > 0 && (
            <Button size="sm" color="secondary" outline onClick={() => setOpen((v) => !v)}>{open ? "Ocultar" : "Ver lista"}</Button>
          )}
        </span>
      </div>
      {msg && <div className="cp-muted" style={{ marginTop: 6 }}>{msg}</div>}
      {open && pendientes > 0 && (
        <div style={{ overflowX: "auto", marginTop: 10 }}>
          <table className="table table-sm" style={{ fontSize: "0.85rem", marginBottom: 0 }}>
            <thead><tr><th>Código</th><th>Proyecto</th><th>Producto anterior</th><th>Modalidad anterior</th><th>Por qué no se clasificó</th></tr></thead>
            <tbody>
              {excepciones.map((e) => (
                <tr key={e.proyecto_code}>
                  <td>{e.proyecto_code}</td><td>{e.name}</td><td>{e.producto_anterior || "—"}</td><td>{e.modalidad_anterior || "—"}</td><td>{e.motivo}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="cp-muted" style={{ marginTop: 6 }}>Para clasificarlos: Proyectos → General → editar el proyecto → bloque Clasificación de producto.</div>
        </div>
      )}
    </div>
  );
}

export default function ClasificacionProductos() {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [adding, setAdding] = useState(null); // "parentPk:nivel" | "root:1"
  const successTimer = useRef(null);

  const flash = useCallback((msg) => {
    setSuccess(msg);
    if (successTimer.current) clearTimeout(successTimer.current);
    successTimer.current = setTimeout(() => setSuccess(""), 2500);
  }, []);

  useEffect(() => () => { if (successTimer.current) clearTimeout(successTimer.current); }, []);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await axios.get(API_URL_clasificacion_productos, { headers: getAuthHeaders() });
      setNodes(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(errorMessage(e, "No se pudo cargar la clasificación de productos."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Index: children by parent pk (null for roots), sorted by orden/nombre.
  const byParent = useMemo(() => {
    const m = new Map();
    nodes.forEach((n) => {
      const key = n.parent ?? null;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(n);
    });
    m.forEach((arr) => arr.sort(sortNodes));
    return m;
  }, [nodes]);

  const byPk = useMemo(() => {
    const m = new Map();
    nodes.forEach((n) => m.set(n.pk, n));
    return m;
  }, [nodes]);

  // ---- Search -------------------------------------------------------------
  const q = normalize(query);
  const search = useMemo(() => {
    if (!q) return null;
    const matches = new Set();
    nodes.forEach((n) => {
      if (normalize(n.nombre).includes(q) || normalize(n.grupo).includes(q)) matches.add(n.pk);
    });
    // visible = matches, plus all ancestors of matches, plus all descendants of matches
    const visible = new Set();
    const expand = new Set();
    const addDescendants = (pk) => {
      (byParent.get(pk) || []).forEach((c) => { visible.add(c.pk); addDescendants(c.pk); });
    };
    matches.forEach((pk) => {
      visible.add(pk);
      let cur = byPk.get(pk);
      while (cur && cur.parent != null) {
        visible.add(cur.parent);
        expand.add(cur.parent);
        cur = byPk.get(cur.parent);
      }
      const self = byPk.get(pk);
      if (self && self.nivel <= NIVEL.CATEGORIA) expand.add(pk);
      addDescendants(pk);
    });
    return { visible, expand };
  }, [q, nodes, byParent, byPk]);

  const childrenOf = useCallback(
    (pk) => {
      const kids = byParent.get(pk) || [];
      if (!search) return kids;
      return kids.filter((n) => search.visible.has(n.pk));
    },
    [byParent, search]
  );

  const effectiveExpanded = useMemo(() => {
    if (!search) return expanded;
    const s = new Set(expanded);
    search.expand.forEach((pk) => s.add(pk));
    return s;
  }, [expanded, search]);

  const toggleOpen = useCallback((pk) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(pk)) next.delete(pk); else next.add(pk);
      return next;
    });
  }, []);

  const productos = useMemo(() => childrenOf(null).filter((n) => n.nivel === NIVEL.PRODUCTO), [childrenOf]);

  const expandAll = () => setExpanded(new Set(nodes.filter((n) => n.nivel <= NIVEL.CATEGORIA).map((n) => n.pk)));
  const collapseAll = () => setExpanded(new Set());

  // ---- Mutations ----------------------------------------------------------
  // Each returns an error string (shown inline) or null on success.
  const actions = useMemo(() => ({
    create: async (payload) => {
      try {
        const res = await axios.post(API_URL_clasificacion_productos, payload, { headers: getAuthHeaders() });
        setNodes((prev) => [...prev, res.data]);
        if (payload.parent != null) setExpanded((prev) => new Set(prev).add(payload.parent));
        if (payload.nivel === NIVEL.PRODUCTO && res.data?.pk != null) {
          setExpanded((prev) => new Set(prev).add(res.data.pk));
        }
        flash(`${NIVEL_LABEL[payload.nivel]} "${payload.nombre}" agregado.`);
        return null;
      } catch (e) {
        return errorMessage(e, "No se pudo guardar.");
      }
    },
    rename: async (node, nombre) => {
      try {
        const res = await axios.patch(`${API_URL_clasificacion_productos}${node.pk}/`, { nombre }, { headers: getAuthHeaders() });
        setNodes((prev) => prev.map((n) => (n.pk === node.pk ? { ...n, ...res.data } : n)));
        flash("Nombre actualizado.");
        return null;
      } catch (e) {
        return errorMessage(e, "No se pudo cambiar el nombre.");
      }
    },
    toggle: async (node) => {
      setError("");
      try {
        const res = await axios.patch(`${API_URL_clasificacion_productos}${node.pk}/`, { activo: !node.activo }, { headers: getAuthHeaders() });
        setNodes((prev) => prev.map((n) => (n.pk === node.pk ? { ...n, ...res.data } : n)));
        flash(res.data?.activo ? `"${node.nombre}" vuelve a estar activo.` : `"${node.nombre}" quedó inactivo.`);
      } catch (e) {
        setError(errorMessage(e, "No se pudo cambiar el estado."));
      }
    },
    remove: async (node) => {
      setError("");
      try {
        await axios.delete(`${API_URL_clasificacion_productos}${node.pk}/`, { headers: getAuthHeaders() });
        setNodes((prev) => prev.filter((n) => n.pk !== node.pk));
        flash(`"${node.nombre}" borrado.`);
        return true;
      } catch (e) {
        setError(errorMessage(e, "No se pudo borrar. Si está en uso, desactívalo en lugar de borrarlo."));
        return false;
      }
    },
  }), [flash]);

  const isAddingProducto = adding === "root:1";

  return (
    <Page>
      <style>{CSS}</style>
      <PageHeader
        title="Clasificación de productos"
        subtitle="Productos, categorías, subproductos, modalidades y tipos que se usan al dar de alta un proyecto."
        actions={
          <Button color="primary" onClick={() => setAdding("root:1")} disabled={isAddingProducto}>
            <IconPlus /> Producto
          </Button>
        }
      />

      <div className="cp-note">
        Esta clasificación alimenta los campos Producto, Categoría, Subproducto, Modalidad y Tipo al dar de alta un proyecto.
        Los elementos inactivos dejan de aparecer en las listas pero se conservan en los proyectos que ya los usan.
      </div>

      <ExcepcionesMigracion />

      {error && <Alert color="danger" toggle={() => setError("")}>{error}</Alert>}
      {success && <Alert color="success" style={{ padding: "6px 12px" }}>{success}</Alert>}

      <div className="cp-toolbar">
        <Input
          className="cp-search"
          type="search"
          placeholder="Buscar por nombre…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button color="secondary" outline size="sm" onClick={expandAll}>Expandir todo</Button>
        <Button color="secondary" outline size="sm" onClick={collapseAll}>Contraer todo</Button>
        {!loading && (
          <span className="cp-muted" style={{ marginLeft: "auto" }}>
            {nodes.length} elementos · {nodes.filter((n) => !n.activo).length} inactivos
          </span>
        )}
      </div>

      {isAddingProducto && (
        <div className="cp-producto" style={{ marginBottom: 12 }}>
          <div className="cp-producto-body">
            <div className="cp-muted" style={{ marginBottom: 4 }}>Nuevo producto</div>
            <AddForm nivel={NIVEL.PRODUCTO} parent={null} onCreate={actions.create} onCancel={() => setAdding(null)} />
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5"><Spinner color="danger" /></div>
      ) : productos.length === 0 ? (
        <div className="cp-note" style={{ textAlign: "center" }}>
          {q ? "Ningún elemento coincide con la búsqueda." : "Todavía no hay productos. Agrega el primero con el botón \"+ Producto\"."}
        </div>
      ) : (
        <div className="cp-tree">
          {productos.map((p) => (
            <ProductoBlock
              key={p.pk}
              node={p}
              childrenOf={childrenOf}
              actions={actions}
              expanded={effectiveExpanded}
              toggleOpen={toggleOpen}
              adding={adding}
              setAdding={setAdding}
            />
          ))}
        </div>
      )}
    </Page>
  );
}
